import { useEffect, useCallback } from 'react';
import { logger } from '@/utils/logger';
import { useErrorHandler } from '@/hooks/useErrorHandler';

// Web Vitals type definitions
interface Metric {
  name: string;
  value: number;
  delta: number;
  id: string;
}

interface UseWebVitalsOptions {
  reportWebVitals?: (metric: Metric) => void;
  debug?: boolean;
}

// Simplified web vitals hook using native Performance Observer API with proper error handling
export const useWebVitals = ({ reportWebVitals, debug = false }: UseWebVitalsOptions = {}) => {
  const { logError } = useErrorHandler({ 
    component: 'WebVitals',
    showToast: false 
  });

  const measureWebVital = useCallback((entryType: string, callback: (metric: Metric) => void) => {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const metric: Metric = {
              name: entryType,
              value: entry.startTime || 0,
              delta: entry.startTime || 0,
              id: `${entryType}-${Date.now()}`
            };
            
            if (debug) {
              logger.debug(`Web Vital: ${entryType}`, { 
                component: 'WebVitals',
                metric: entryType 
              }, metric);
            }
            
            callback(metric);
          }
        });
        observer.observe({ entryTypes: [entryType] });
      } catch (e) {
        logError(`Failed to observe ${entryType}`, {
          entryType,
          error: e instanceof Error ? e.message : String(e)
        });
      }
    }
  }, [debug, logError]);

  useEffect(() => {
    if (!reportWebVitals && !debug) return;

    // Measure key web vitals
    measureWebVital('largest-contentful-paint', (metric) => {
      if (reportWebVitals) reportWebVitals(metric);
      if (debug) {
        logger.info(`LCP: ${metric.value}ms`, { 
          component: 'WebVitals',
          metric: 'LCP'
        }, metric);
      }
    });

    measureWebVital('first-input-delay', (metric) => {
      if (reportWebVitals) reportWebVitals(metric);
      if (debug) {
        logger.info(`FID: ${metric.value}ms`, { 
          component: 'WebVitals',
          metric: 'FID'
        }, metric);
      }
    });

    measureWebVital('cumulative-layout-shift', (metric) => {
      if (reportWebVitals) reportWebVitals(metric);
      if (debug) {
        logger.info(`CLS: ${metric.value}`, { 
          component: 'WebVitals',
          metric: 'CLS'
        }, metric);
      }
    });

    // TTFB calculation
    if (performance.getEntriesByType) {
      const navigationEntries = performance.getEntriesByType('navigation');
      if (navigationEntries.length > 0) {
        const navEntry = navigationEntries[0] as PerformanceNavigationTiming;
        const ttfb = navEntry.responseStart - navEntry.requestStart;
        
        const ttfbMetric: Metric = {
          name: 'TTFB',
          value: ttfb,
          delta: ttfb,
          id: `ttfb-${Date.now()}`
        };

        if (reportWebVitals) reportWebVitals(ttfbMetric);
        if (debug) {
          logger.info(`TTFB: ${ttfb}ms`, { 
            component: 'WebVitals',
            metric: 'TTFB'
          }, ttfbMetric);
        }
      }
    }
  }, [measureWebVital, reportWebVitals, debug]);
};

// Type declaration for gtag  
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}